import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { assignmentService } from '../services/api';
import { Button, Modal, Field, Input, Textarea, Badge } from './ui';
import { isImageAttachment, attachmentFileName } from '../utils/attachments';

function AttachmentLink({ url, t }) {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:underline"
    >
      {isImageAttachment(url) ? (
        <img src={url} alt="" className="w-5 h-5 rounded object-cover" />
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )}
      {attachmentFileName(url) || t('Attachment')}
    </a>
  );
}

function AssignmentIcon({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 014-4h3m0 0l-3-3m3 3l-3 3M13 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V9a2 2 0 00-2-2h-2" />
    </svg>
  );
}

function SubmissionsPanel({ assignment, onGraded, t }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await assignmentService.getSubmissions(assignment.id);
      const list = res.data?.results || res.data || [];
      setSubmissions(list);
    } catch (err) {
      console.warn('Failed to load submissions', err);
    } finally {
      setLoading(false);
    }
  }, [assignment.id]);

  useEffect(() => { load(); }, [load]);

  const handleGrade = async (submissionId) => {
    const draft = drafts[submissionId] || {};
    if (draft.grade === undefined || draft.grade === '') return;
    setSavingId(submissionId);
    try {
      await assignmentService.gradeSubmission(submissionId, { grade: draft.grade, feedback: draft.feedback || '' });
      await load();
      onGraded && onGraded();
    } catch (err) {
      console.error('Failed to grade submission', err);
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <div className="text-sm text-gray-400 py-4">{t('Loading...')}</div>;

  if (submissions.length === 0) {
    return <div className="text-sm text-gray-400 py-4">{t('No submissions yet')}</div>;
  }

  return (
    <div className="space-y-3 mt-3">
      {submissions.map((s) => {
        const draft = drafts[s.id] || { grade: s.grade ?? '', feedback: s.feedback || '' };
        return (
          <div key={s.id} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-sm text-gray-900 truncate">
                  {s.student.first_name} {s.student.last_name}
                </div>
                <div className="text-xs text-gray-400">{new Date(s.submitted_at).toLocaleString()}</div>
              </div>
              <a href={s.file} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary-600 hover:underline shrink-0">
                {t('View File')}
              </a>
            </div>
            {s.note && <p className="text-xs text-gray-600 mt-2 whitespace-pre-line">{s.note}</p>}

            {s.status === 'graded' ? (
              <div className="mt-2 flex items-center gap-2">
                <Badge tone="green">{t('{{grade}}/{{max}}', { grade: s.grade, max: s.max_points })}</Badge>
                {s.feedback && <span className="text-xs text-gray-500">{s.feedback}</span>}
              </div>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={s.max_points}
                  placeholder={t('Grade')}
                  value={draft.grade}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [s.id]: { ...draft, grade: e.target.value } }))}
                  className="w-20 text-sm border border-gray-200 rounded-lg px-2 py-1"
                />
                <span className="text-xs text-gray-400">/ {s.max_points}</span>
                <input
                  type="text"
                  placeholder={t('Feedback (optional)')}
                  value={draft.feedback}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [s.id]: { ...draft, feedback: e.target.value } }))}
                  className="flex-1 min-w-[8rem] text-sm border border-gray-200 rounded-lg px-2 py-1"
                />
                <Button size="sm" onClick={() => handleGrade(s.id)} loading={savingId === s.id} disabled={draft.grade === ''}>
                  {t('Save Grade')}
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function LessonAssignments({ lessonId, isTeacher }) {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', due_date: '', max_points: 100, attachment: null });
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await assignmentService.getAssignments(lessonId);
      const list = res.data?.results || res.data || [];
      setAssignments(list);
    } catch (err) {
      console.warn('Failed to load assignments', err);
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append('lesson', lessonId);
      fd.append('title', form.title);
      fd.append('description', form.description);
      if (form.due_date) fd.append('due_date', form.due_date);
      fd.append('max_points', parseInt(form.max_points, 10) || 100);
      if (form.attachment) fd.append('attachment', form.attachment);
      // DRF's BooleanField treats a key *missing* from multipart form data as
      // an explicit False (HTML checkbox semantics), not "use the model
      // default" — has to be sent explicitly or new assignments silently
      // save unpublished and never show up for students.
      fd.append('is_published', 'true');
      await assignmentService.createAssignment(fd);
      setShowCreate(false);
      setForm({ title: '', description: '', due_date: '', max_points: 100, attachment: null });
      await load();
    } catch (err) {
      console.error('Failed to create assignment', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await assignmentService.deleteAssignment(id);
      await load();
    } catch (err) {
      console.error('Failed to delete assignment', err);
    }
  };

  const handleSubmit = async (assignment, file) => {
    if (!file) return;
    setUploadingId(assignment.id);
    try {
      const fd = new FormData();
      fd.append('assignment', assignment.id);
      fd.append('file', file);
      if (notes[assignment.id]) fd.append('note', notes[assignment.id]);
      await assignmentService.submitAssignment(fd);
      await load();
    } catch (err) {
      console.error('Failed to submit assignment', err);
    } finally {
      setUploadingId(null);
    }
  };

  if (loading) return null;
  if (!isTeacher && assignments.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <AssignmentIcon className="w-5 h-5 mr-2" />
          {t('Assignments')}
        </h2>
        {isTeacher && (
          <button onClick={() => setShowCreate(true)} className="text-xs font-medium text-primary-600 hover:underline">
            + {t('Add Assignment')}
          </button>
        )}
      </div>

      {assignments.length === 0 ? (
        <p className="text-sm text-gray-400">{t('No assignments yet')}</p>
      ) : (
        <div className="space-y-3">
          {assignments.map((a) => (
            <div key={a.id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-medium text-sm text-gray-900">{a.title}</h3>
                  {a.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{a.description}</p>}
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
                    <span>{t('{{points}} pts', { points: a.max_points })}</span>
                    {a.due_date && <span>• {t('Due')} {new Date(a.due_date).toLocaleDateString()}</span>}
                  </div>
                  {a.attachment && (
                    <div className="mt-1.5">
                      <AttachmentLink url={a.attachment} t={t} />
                    </div>
                  )}
                </div>
                {isTeacher && (
                  <button onClick={() => handleDelete(a.id)} className="text-gray-300 hover:text-red-500 shrink-0" title={t('Delete')}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>

              {isTeacher ? (
                <>
                  <button
                    onClick={() => setExpandedId(expandedId === a.id ? null : a.id)}
                    className="mt-2 text-xs font-medium text-primary-600 hover:underline flex items-center gap-1"
                  >
                    {t('{{count}} submissions', { count: a.submissions_count })} ({t('{{count}} graded', { count: a.graded_count })})
                    <svg className={`w-3 h-3 transition-transform ${expandedId === a.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedId === a.id && (
                    <SubmissionsPanel assignment={a} onGraded={load} t={t} />
                  )}
                </>
              ) : (
                <div className="mt-3">
                  {a.my_submission ? (
                    a.my_submission.status === 'graded' ? (
                      <div className="flex items-center gap-2">
                        <Badge tone="green">{t('{{grade}}/{{max}}', { grade: a.my_submission.grade, max: a.max_points })}</Badge>
                        {a.my_submission.feedback && <span className="text-xs text-gray-500">{a.my_submission.feedback}</span>}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        <Badge tone="blue">{t('Submitted')}</Badge>
                        <a href={a.my_submission.file} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                          {t('View File')}
                        </a>
                        <label className="text-primary-600 hover:underline cursor-pointer">
                          {t('Resubmit')}
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => handleSubmit(a, e.target.files?.[0])}
                          />
                        </label>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="text"
                        placeholder={t('Note (optional)')}
                        value={notes[a.id] || ''}
                        onChange={(e) => setNotes((prev) => ({ ...prev, [a.id]: e.target.value }))}
                        className="flex-1 min-w-[8rem] text-xs border border-gray-200 rounded-lg px-2 py-1.5"
                      />
                      <label className={`px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-medium cursor-pointer hover:bg-primary-700 transition shrink-0 ${uploadingId === a.id ? 'opacity-60 pointer-events-none' : ''}`}>
                        {uploadingId === a.id ? t('Uploading...') : t('Upload Submission')}
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => handleSubmit(a, e.target.files?.[0])}
                        />
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title={t('Add Assignment')}>
        <div className="space-y-4">
          <Field label={t('Title')} required>
            <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} autoFocus />
          </Field>
          <Field label={t('Description')}>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('Due Date (optional)')}>
              <Input type="datetime-local" value={form.due_date} onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))} />
            </Field>
            <Field label={t('Max Points')}>
              <Input type="number" min={1} value={form.max_points} onChange={(e) => setForm((p) => ({ ...p, max_points: e.target.value }))} />
            </Field>
          </div>
          <Field label={t('Attachment (optional)')}>
            <input
              type="file"
              onChange={(e) => setForm((p) => ({ ...p, attachment: e.target.files?.[0] || null }))}
              className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-primary-50 file:text-primary-600 file:text-sm file:font-medium hover:file:bg-primary-100"
            />
            <p className="text-xs text-gray-400 mt-1">{t('Instructions, a template, or a rubric for students')}</p>
          </Field>
          <Button className="w-full" onClick={handleCreate} loading={creating} disabled={!form.title.trim()}>
            {t('Create Assignment')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
